import { Text } from "@musa.tech/design-system";
import type { MonthlyCollectsByOrigin } from "@/integrations/agreements/client";
import { CollectsByOriginChart } from "./CollectsByOriginChart";
import {
  COLLECTS_BY_ORIGIN_SERIES,
  isCollectsByOriginEmpty,
} from "./collectsByOriginSeries";

interface CollectsByOriginWidgetProps {
  entries: MonthlyCollectsByOrigin[];
}

/**
 * Widget "Coletas recorrentes x solicitadas sob demanda" (Figma node 345:16164)
 * — quantas coletas realizadas vieram da grade do contrato e quantas foram
 * avulsas, nos quatro meses mais recentes.
 *
 * A janela é fixa e **ignora o filtro de período** da tela; os demais filtros
 * valem. O número não bate com os indicadores da tela de Coletas sob demanda:
 * lá se conta solicitação, aqui coleta, e uma solicitação de caçamba vira uma
 * coleta por recipiente.
 *
 * O Figma escreve "solictadas" — grafia corrigida aqui de propósito.
 */
export const CollectsByOriginWidget = ({ entries }: CollectsByOriginWidgetProps) => {
  const isEmpty = !entries.length || isCollectsByOriginEmpty(entries);

  return (
    <div className="flex h-full w-full flex-col gap-4 rounded-2xl bg-white p-6 transition-shadow duration-200 hover:shadow-md motion-reduce:transition-none">
      <Text className="text-xl font-light leading-6 text-black">
        Coletas recorrentes x solicitadas sob demanda
      </Text>

      {/* Vazio some só com o gráfico: título e legenda ficam, senão o card
          órfão na grade parece um erro de render. */}
      {isEmpty ? (
        <div className="flex flex-1 items-center justify-center">
          <Text variant="body" size="small" className="text-neutral-600">
            Nenhuma coleta realizada no período
          </Text>
        </div>
      ) : (
        // `shrink-0`, não `flex-1`: `flex-1` tem flex-basis 0%, que ignora a
        // `height` inline no cálculo de layout — o valor virava "o que sobra
        // do container", não 278px de verdade. Fixo de propósito para o
        // gráfico ter sempre a mesma altura, com ou sem vizinho de linha.
        <div className="min-h-0 shrink-0" style={{ height: "278px" }}>
          <CollectsByOriginChart entries={entries} />
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-6">
        {COLLECTS_BY_ORIGIN_SERIES.map((series) => (
          <div key={series.key} className="flex items-center gap-1">
            <div className={`size-5 shrink-0 rounded-lg ${series.swatchClassName}`} />
            <Text className="whitespace-nowrap text-xs leading-5 text-neutral-500">
              {series.label}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};
